import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { Login } from './Login';
import { AdminLayout } from './AdminLayout';
import { TenantsPage } from './TenantsPage';
import { UsersPage } from './UsersPage';
import { TenantDetailPage } from './TenantDetailPage';
import { AuditPage } from './AuditPage';
import { AuditBillingPage } from './AuditBillingPage';
import { SettingsPage } from './SettingsPage';
import { SuperAdminsPage } from './SuperAdminsPage';
import { ApiKeysPage } from './ApiKeysPage';
import { AppVersionGatePage } from './AppVersionGatePage';
import { CountriesPage } from './CountriesPage';
import { BillingOverviewPage } from './BillingOverviewPage';
import { BillingSubscriptionsPage } from './BillingSubscriptionsPage';
import { InvoicesPage } from './InvoicesPage';
import { InvoiceDetailPage } from './InvoiceDetailPage';
import { PaymentsPage } from './PaymentsPage';
import { PendingPage } from './PendingPage';
import { CourtesyMigrationPage } from './CourtesyMigrationPage';
import { PaymentMethodsPage } from './PaymentMethodsPage';
import { BillingEmailsPage } from './BillingEmailsPage';
import { AddonsPage } from './AddonsPage';
import { ModuleCatalogImportPage } from './ModuleCatalogImportPage';
import { MarketsPage } from './MarketsPage';
import { PspListPage } from '@/features/psp/PspListPage';
import { PspEditorPage } from '@/features/psp/PspEditorPage';
import { SellerEntitiesPage } from '@/features/sellerEntities/SellerEntitiesPage';
import { SellerEntityEditorPage } from '@/features/sellerEntities/SellerEntityEditorPage';
import { PromotionsPage } from './PromotionsPage';
import { TaxDataPage } from './TaxDataPage';
import { TransactionsPage } from './TransactionsPage';
import { RefundsPage } from './RefundsPage';
import { DisputesPage } from './DisputesPage';
import { FinanceAccountsPage } from './FinanceAccountsPage';
import { FinanceOverviewPage } from './FinanceOverviewPage';
import { FinanceCashflowPage } from './FinanceCashflowPage';
import { FinanceBalancePage } from './FinanceBalancePage';
import { FinanceEntriesPage } from './FinanceEntriesPage';
import { FinanceImportsPage } from './FinanceImportsPage';
import { FinanceImportFlowPage } from './FinanceImportFlowPage';
import { FinanceDashboardPage } from '@/features/dashboard-finance/FinanceDashboardPage';
import { SalesDashboardPage } from '@/features/dashboard-sales/SalesDashboardPage';
import { UsageBasicDashboardPage } from '@/features/dashboard-usage-basic/UsageBasicDashboardPage';
import { CohortsDashboardPage } from '@/features/dashboard-cohorts/CohortsDashboardPage';
import { FunnelDashboardPage } from '@/features/dashboard-funnel/FunnelDashboardPage';
import { ChurnDashboardPage } from '@/features/dashboard-churn/ChurnDashboardPage';
import { HealthDashboardPage } from '@/features/dashboard-health/HealthDashboardPage';
import { CustomersDashboardPage } from '@/features/dashboard-customers/CustomersDashboardPage';
import { UsageDashboardPage } from '@/features/dashboard-usage/UsageDashboardPage';
import { OperationDashboardPage } from '@/features/dashboard-operation/OperationDashboardPage';
import { ProductDashboardPage } from '@/features/dashboard-product/ProductDashboardPage';
import { AdminUsageDashboardPage } from '@/features/admin-usage/AdminUsageDashboardPage';
import { AlertsPage } from '@/features/dashboard-alerts/AlertsPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { ContactMessagesPage } from '@/features/contact/ContactMessagesPage';
import { NotificationsBroadcastPage } from './NotificationsBroadcastPage';
import { NotificationPrefsPage } from './NotificationPrefsPage';
import { RequireSuperAdmin } from '@/auth/RequireSuperAdmin';

/**
 * Tabela de rotas do admin. `/login` é pública; tudo o mais passa pelo
 * `RequireSuperAdmin` e renderiza dentro do `AdminLayout` (header + outlet).
 *
 * Origem: sem issue — commit 4dbd8df25, 2026-04-26 (Sprint 2, scaffolding
 * Vite + React).
 *
 * Usado por: `router` (abaixo), que o `main.tsx` entrega ao `RouterProvider`;
 * `routes/router.test.tsx`. Cada tela daqui tem docstring com "Usado por"
 * apontando para a sua rota.
 *
 * Dicas: rota nova precisa também do item no menu (`NAV_GROUPS` em
 * `AdminLayout.tsx`) e, se tiver formulário, da spec em `docs/screens/`. URL
 * antiga que saiu de uso vira `<Navigate replace>` (ex.: `billing/plan-prices`),
 * nunca 404 — operador tem favorito.
 */
const routes: RouteObject[] = [
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/',
    element: (
      <RequireSuperAdmin>
        <AdminLayout />
      </RequireSuperAdmin>
    ),
    children: [
      { index: true, element: <Navigate to="/tenants" replace /> },
      { path: 'tenants', element: <TenantsPage /> },
      // Issue #257 — lista global de pessoas (uma linha por uid, cross-tenant).
      { path: 'users', element: <UsersPage /> },
      { path: 'tenants/:id', element: <TenantDetailPage /> },
      { path: 'audit', element: <AuditPage /> },
      // Sprint 35 PR H1 — auditoria financeira: eventos /audit/billing/items
      // com filtros (tenant/action/actor/data/extEventId), drawer com
      // before/after JSON, export CSV super-admin.
      { path: 'audit/billing', element: <AuditBillingPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'settings/notifications', element: <NotificationPrefsPage /> },
      { path: 'settings/super-admins', element: <SuperAdminsPage /> },
      // #263 — integrações da API pública readonly (credencial, alcance de
      // empresas, scopes e limites por parceiro).
      { path: 'settings/api-keys', element: <ApiKeysPage /> },
      // #655 — kill switch de versão: apertar o `minBuild` de uma plataforma.
      // Antes desta tela isso era editar `/appConfig/{platform}` no console.
      { path: 'settings/app-versions', element: <AppVersionGatePage /> },
      { path: 'countries', element: <CountriesPage /> },
      // Billing — Sprint 16
      { path: 'billing', element: <Navigate to="/billing/pending" replace /> },
      { path: 'billing/overview/:tenantId', element: <BillingOverviewPage /> },
      // #78 — Recebíveis de assinaturas: calendário semanal previsto × realizado.
      { path: 'billing/subscriptions', element: <BillingSubscriptionsPage /> },
      { path: 'billing/invoices', element: <InvoicesPage /> },
      { path: 'billing/invoices/:tenantId', element: <InvoicesPage /> },
      {
        path: 'billing/invoices/:tenantId/:invoiceId',
        element: <InvoiceDetailPage />,
      },
      { path: 'billing/payments', element: <PaymentsPage /> },
      { path: 'billing/payments/:tenantId', element: <PaymentsPage /> },
      { path: 'billing/pending', element: <PendingPage /> },
      // #679 F7 (#738) — migração da cortesia/demo para o tipo comercial, em etapas.
      { path: 'billing/courtesy-migration', element: <CourtesyMigrationPage /> },
      { path: 'billing/methods/:tenantId', element: <PaymentMethodsPage /> },
      { path: 'billing/emails', element: <BillingEmailsPage /> },
      // #679 F4 (9.17): o catálogo de Prices de lista saiu de uso — o preço é
      // por tenant, na venda do CRM. Os docs de `/config/planPrices` ficam como
      // índice legado de leitura do servidor, sem tela.
      { path: 'billing/plan-prices', element: <Navigate to="/billing/addons" replace /> },
      // Sprint 35 PR G1 — CRUD super-admin de add-ons (SKU livre via regex).
      { path: 'billing/addons', element: <AddonsPage /> },
      { path: 'billing/addons/import', element: <ModuleCatalogImportPage /> },
      // Sprint 35 PR G2 — CRUD super-admin de cupons + promo codes +
      // lista de tenants com Lifetime Free.
      { path: 'billing/promotions', element: <PromotionsPage /> },
      // Sprint 35 PR G5 — CRUD mercado por país (saleable + provider).
      { path: 'billing/markets', element: <MarketsPage /> },
      // #756 — todo setup de PSP (catálogo, produção/sandbox, credenciais).
      { path: 'psp', element: <PspListPage /> },
      { path: 'psp/:id', element: <PspEditorPage /> },
      // #756 (A7) — entidades vendedoras: a PJ BR com os dados de cobrança e
      // nota (spec `docs/screens/billing-seller-entities.md`).
      { path: 'billing/seller-entities', element: <SellerEntitiesPage /> },
      { path: 'billing/seller-entities/:id', element: <SellerEntityEditorPage /> },
      { path: 'billing/tax/:tenantId', element: <TaxDataPage /> },
      // Transacional do PSP (Sprint 5 PR H) — agora no grupo "Cobrança"
      // (épico #72); rotas mantidas em /finance/* (migração de URL adiada).
      { path: 'finance/transactions', element: <TransactionsPage /> },
      { path: 'finance/refunds', element: <RefundsPage /> },
      { path: 'finance/disputes', element: <DisputesPage /> },
      // Financeiro INTERNO da empresa (épico #72, PR A #73 + PR B #74).
      // PR D #76 — análise: Visão geral (/finance) + Caixa + Balanço.
      { path: 'finance', element: <FinanceOverviewPage /> },
      { path: 'finance/cashflow', element: <FinanceCashflowPage /> },
      { path: 'finance/balance', element: <FinanceBalancePage /> },
      { path: 'finance/entries', element: <FinanceEntriesPage /> },
      // PR C #75 — import por OCR: histórico + fluxo upload→preview→confirmar.
      { path: 'finance/imports', element: <FinanceImportsPage /> },
      { path: 'finance/imports/new', element: <FinanceImportFlowPage /> },
      { path: 'finance/imports/:importId', element: <FinanceImportFlowPage /> },
      { path: 'finance/accounts', element: <FinanceAccountsPage /> },
      // BI
      { path: 'dashboard/finance', element: <FinanceDashboardPage /> },
      { path: 'dashboard/sales', element: <SalesDashboardPage /> },
      { path: 'dashboard/usage-basic', element: <UsageBasicDashboardPage /> },
      { path: 'dashboard/cohorts', element: <CohortsDashboardPage /> },
      { path: 'dashboard/funnel', element: <FunnelDashboardPage /> },
      { path: 'dashboard/churn', element: <ChurnDashboardPage /> },
      { path: 'dashboard/health', element: <HealthDashboardPage /> },
      { path: 'dashboard/customers', element: <CustomersDashboardPage /> },
      { path: 'dashboard/usage', element: <UsageDashboardPage /> },
      { path: 'dashboard/operation', element: <OperationDashboardPage /> },
      { path: 'dashboard/product', element: <ProductDashboardPage /> },
      { path: 'dashboard/admin-usage', element: <AdminUsageDashboardPage /> },
      { path: 'dashboard/alerts', element: <AlertsPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'contact/messages', element: <ContactMessagesPage /> },
      { path: 'notifications/broadcast', element: <NotificationsBroadcastPage /> },
    ],
  },
];

// Bug corrigido em 2026-10-03 14:38 +0200 (#795, OBS-3): nenhuma rota tinha
// `errorElement`, então o crash de render de qualquer tela caía no boundary
// interno do React Router 7 — só `console.error`, stack na tela do operador e
// NENHUM evento (o `ErrorBoundary` do `main.tsx` fica por fora do
// `RouterProvider` e não o recebe; o `admin_render_crash` nunca saía para crash
// de tela). Esta rota-raiz pathless envolve TODAS as rotas com o
// `RouteErrorBoundary` (`admin_route_crash`). Não remover nem mover para dentro
// do `AdminLayout`: `/login` ficaria de fora. Irmãos: `app_route_crash`
// (apps/app, WEB-2) e `crm_route_crash` (apps/crm). Teste: `router.test.tsx`.
export const router = createBrowserRouter([{ errorElement: <RouteErrorBoundary />, children: routes }]);
